import { Button, H5, H6, InputNumber } from "@sellar-io/components";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  DialogNavigation,
  DialogContent,
} from "../../../../../components/modal/Dialog";
import { useRemoteFeatureFlags } from "../../../../../hooks/useRemoteFeatureFlags";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Switch } from "@sellar-io/components";
import { ProductCreateRequestTypeProductItemType } from "@sellar-io/api-v2/types";
import { Icon } from "../../../../../components";
import { useEffect } from "react";
import { StockStatusCard } from "../../components/StockStatusCard";
import dayjs from "dayjs";
import { ItemReleaseType } from "./ItemReleaseDialogContent";
import { calculateAvailableStockFromFormInput } from "../../helpers/calculateAvailableStock";

type GeneralReleaseInputType = Required<
  Pick<
    ProductCreateRequestTypeProductItemType,
    "stock" | "maxOrderQuantity" | "canBackorder" | "active"
  >
>;

type PreOrderReleaseInputType = Required<
  Pick<
    ProductCreateRequestTypeProductItemType,
    "stock" | "incomingStock" | "maxOrderQuantity" | "active" | "canBackorder"
  >
>;

const GeneralReleaseInputForm = ({
  loading,
  onBack,
  onFormSubmit,
}: {
  loading: boolean;
  onBack: () => void;
  onFormSubmit: (data: GeneralReleaseInputType) => void;
}) => {
  const remoteFeatureFlag = useRemoteFeatureFlags();
  const { register, handleSubmit, control } = useForm<GeneralReleaseInputType>({
    defaultValues: {
      stock: 0,
      maxOrderQuantity: null,
      canBackorder: false,
      active: true,
    },
    shouldUseNativeValidation: true,
  });

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <H5>Continuous back orders</H5>
          <H6>
            Let customers place orders even if the product is out of stock.
          </H6>
        </div>

        <Controller
          name="canBackorder"
          control={control}
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <Switch
              checked={!!value}
              onChange={(checked) => onChange(checked)}
            />
          )}
        />
      </div>

      <div>
        <InputLabel>Total (on hand) stock</InputLabel>
        <InputContainer className={"w-full"}>
          <InputNumber
            {...register("stock", {
              required: true,
              valueAsNumber: true,
            })}
            required={true}
          />
        </InputContainer>
      </div>

      {remoteFeatureFlag.isActive("preOrders") && (
        <div>
          <InputLabel>Maximum order quantity</InputLabel>
          <InputContainer className={"w-full"}>
            <InputNumber
              {...register("maxOrderQuantity", {
                valueAsNumber: true,
              })}
              min={1}
            />
          </InputContainer>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button
          color="tertiary"
          disabled={loading}
          type="button"
          onClick={onBack}
        >
          <div className="flex">
            <Icon icon="CHEVRON_LEFT" />
            <div>Back</div>
          </div>
        </Button>
        <Button disabled={loading} type="submit">
          Confirm
        </Button>
      </div>
    </form>
  );
};

const PreOrderReleaseInputForm = ({
  availableFrom,
  onFormSubmit,
  onBack,
  loading,
}: {
  availableFrom: Date;
  onFormSubmit: (data: PreOrderReleaseInputType) => void;
  onBack: () => void;
  loading: boolean;
}) => {
  const { register, handleSubmit, control, setValue } =
    useForm<PreOrderReleaseInputType>({
      defaultValues: {
        stock: 0,
        incomingStock: 0,
        maxOrderQuantity: null,
        canBackorder: false,
        active: true,
      },
      shouldUseNativeValidation: true,
    });

  const canBackorder = useWatch({
    control,
    name: "canBackorder",
  });
  const stock = useWatch({
    control,
    name: "stock",
  });
  const incomingStock = useWatch({
    control,
    name: "incomingStock",
  });

  const availableStock = calculateAvailableStockFromFormInput({
    stock,
    incomingStock,
    committedStock: 0,
  });

  useEffect(() => {
    if (canBackorder) {
      setValue("incomingStock", 0);
    }
  }, [canBackorder, setValue]);

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <H5>Continuous back orders</H5>
          <H6>
            Let customers place orders even if the product is out of stock.
          </H6>
        </div>

        <Controller
          name="canBackorder"
          control={control}
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <Switch
              checked={!!value}
              onChange={(checked) => onChange(checked)}
            />
          )}
        />
      </div>

      <div>
        <InputLabel>Total (on hand) stock</InputLabel>
        <InputContainer className={"w-full"}>
          <InputNumber
            {...register("stock", {
              required: true,
              valueAsNumber: true,
            })}
            required={true}
          />
        </InputContainer>
      </div>

      <div className="flex flex-col gap-1">
        <div>
          <InputLabel>*Incoming stock</InputLabel>
          <InputContainer className={"w-full"}>
            <InputNumber
              {...register("incomingStock", {
                valueAsNumber: true,
              })}
              disabled={canBackorder}
              min={0}
              required={true}
            />
          </InputContainer>
        </div>
        {availableFrom && dayjs(availableFrom).isValid() && (
          <div className={"flex items-center gap-1 text-xs"}>
            <Icon icon={"INFORMATION_OUTLINE"} size={0.6} />
            <div className="text-sm text-zinc-500">
              Let customers order from a limited quantity of stock that will be
              available to deliver from{" "}
              {dayjs(availableFrom).format("DD/MM/YYYY")}.
            </div>
          </div>
        )}
      </div>

      <StockStatusCard
        availableStock={availableStock}
        committedStock={0}
        canBackorder={canBackorder}
      />

      <div>
        <InputLabel>Maximum order quantity</InputLabel>
        <InputContainer className={"w-full"}>
          <InputNumber
            {...register("maxOrderQuantity", {
              valueAsNumber: true,
            })}
            min={1}
          />
        </InputContainer>
      </div>

      <div className="flex items-center justify-between">
        <InputLabel>Visible on platform</InputLabel>
        <Controller
          name="active"
          control={control}
          defaultValue={false}
          render={({ field: { onChange, value } }) => (
            <Switch
              checked={!!value}
              onChange={(checked) => onChange(checked)}
            />
          )}
        />
      </div>

      <div className="mt-4 flex w-full items-center justify-between">
        <Button
          color="tertiary"
          disabled={loading}
          type="button"
          onClick={onBack}
        >
          <div className="flex">
            <Icon icon="CHEVRON_LEFT" />
            <div>Back</div>
          </div>
        </Button>
        <Button disabled={loading} type="submit">
          Confirm
        </Button>
      </div>
    </form>
  );
};

export default function StockManagementDialogContent({
  itemRelease,
  onSubmitStockManagementDetails,
  loading,
  onBack,
  onDismiss,
}: {
  itemRelease: ItemReleaseType;
  onSubmitStockManagementDetails: (
    stockManagementDetails: GeneralReleaseInputType | PreOrderReleaseInputType,
  ) => void;
  loading: boolean;
  onBack: () => void;
  onDismiss: () => void;
}) {
  return (
    <>
      <DialogNavigation
        title="Stock management"
        description="Define how you want to manage your stock."
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        <div className={"flex flex-col gap-4"}>
          {itemRelease.releaseType === "generalRelease" && (
            <GeneralReleaseInputForm
              loading={loading}
              onFormSubmit={onSubmitStockManagementDetails}
              onBack={onBack}
            />
          )}

          {itemRelease.releaseType === "futureRelease" && (
            <PreOrderReleaseInputForm
              availableFrom={itemRelease.availableFrom}
              loading={loading}
              onFormSubmit={onSubmitStockManagementDetails}
              onBack={onBack}
            />
          )}
        </div>
      </DialogContent>
    </>
  );
}
